import * as THREE from "three";
import { Engine } from "../../game/engine";
import { AudioBed } from "../../game/audio";

/**
 * Sigil — path-trace precision, redesigned for 3D depth + engagement.
 * Game 04 of 12. (v2 — feel-pass sweep.)
 *
 * Scoring contract preserved: 5 seed-derived glyphs + 30 Hz dev samples
 * (t, glyph_idx, vertex_idx, dx, dy). Replay validates unchanged. Visual
 * layer rebuilt: 3D tube glyph, sphere traveler, pointer trail dots,
 * pitched camera, per-glyph palette.
 */

export const GLYPHS_PER_RUN = 5;
const SAMPLE_INTERVAL_MS = 33;
const QUANT = 0.05;
const GLYPH_BREAK_MS = 1000;

export interface SigilSample { t: number; glyph_idx: number; vertex_idx: number; dx: number; dy: number; p?: number; }

export interface SigilCallbacks {
  onHud: (glyphIdx: number, lastBucket: string, scoreEstimate: number) => void;
  onGameOver: (result: { samples: SigilSample[]; scoreEstimate: number; bestCombo: number; bucketCounts: Record<string, number>; coarse: boolean; pressureMode: boolean; }) => void;
  /** v5 — fired when a ghost-replay session completes. Lets the host UI
   *  return to the original game-over state without a score submission. */
  onReplayEnd?: () => void;
}

export interface SigilOptions {
  /** v5 — passive replay viewer. When set, the game runs deterministically
   *  off the supplied samples (same seed → same glyphs); pointer input is
   *  ignored, no score is collected, and no run is submitted. */
  ghost?: { samples: SigilSample[]; coarse: boolean };
  /** v5 — opt-in stylus pressure-sensitivity scoring. When true, higher
   *  PointerEvent.pressure widens the per-sample tight bucket by up to
   *  +0.1 deviation. Default false; never auto-enables. */
  pressureMode?: boolean;
}

interface Glyph { idx: number; vertices: { x: number; y: number }[]; speed_vps: number; }

const GLYPH_PALETTES = [
  { line: 0x66e0ff, traveler: 0xff5b3a, emissive: 0xff2010 },
  { line: 0xff77ff, traveler: 0xffd54a, emissive: 0xff8800 },
  { line: 0xa0ffea, traveler: 0xff3df0, emissive: 0xcc1aa0 },
  { line: 0xffd54a, traveler: 0x66ddff, emissive: 0x0080cc },
  { line: 0x66ff88, traveler: 0xff5b3a, emissive: 0xff2010 },
];

function makeRng(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function generateBatch(seed: number): Glyph[] {
  const rng = makeRng(seed);
  const out: Glyph[] = [];
  for (let i = 0; i < GLYPHS_PER_RUN; i++) {
    const N = Math.floor(16 + rng() * 8) + Math.floor(i * 1.5);
    const R = 4 + i * 0.4;
    const lobes = 2 + Math.floor(rng() * 3) + Math.floor(i * 0.5);
    const speed_vps = 4 + rng() * 3 + i * 0.6;
    const amp1 = 0.6 + rng() * 0.6;
    const amp2 = 0.3 + rng() * 0.4;
    const phase1 = rng() * Math.PI * 2;
    const phase2 = rng() * Math.PI * 2;
    const lobes2 = lobes + 1 + Math.floor(rng() * 2);
    const verts: { x: number; y: number }[] = [];
    for (let v = 0; v < N; v++) {
      const theta = (v / N) * Math.PI * 2;
      const r = R + amp1 * Math.sin(lobes * theta + phase1) + amp2 * Math.sin(lobes2 * theta + phase2);
      verts.push({ x: r * Math.cos(theta), y: r * Math.sin(theta) });
    }
    out.push({ idx: i, vertices: verts, speed_vps });
  }
  return out;
}

export class Sigil {
  private engine: Engine;
  private audio = new AudioBed();
  private glyphs: Glyph[];
  private currentGlyph = 0;
  private glyphTube?: THREE.Mesh;
  private glyphMat?: THREE.MeshStandardMaterial;
  /** v5 — per-glyph traveler progress (0..1 along the curve) fed to the
   *  fragment shader so the trailing portion of the tube fades. Rebuilt
   *  per glyph; lives next to glyphMat so disposal cascades naturally. */
  private glyphTravelerU: { value: number } = { value: 0 };
  private traveler: THREE.Mesh;
  private travelerMat: THREE.MeshStandardMaterial;
  private cursor: THREE.Mesh;
  private cursorMat: THREE.MeshBasicMaterial;
  private pointer = new THREE.Vector2();
  private vertexIdxF = 0;
  private glyphStartedAt = 0;
  private inBreak = true;
  private breakEndsAt = 0;
  private samples: SigilSample[] = [];
  private samplerTimer = 0;
  private combo = 0;
  private bestCombo = 0;
  private scoreEstimate = 0;
  private bucketCounts: Record<string, number> = { tight: 0, close: 0, loose: 0, lost: 0 };
  private gameOver = false;
  private cb: SigilCallbacks;
  private canvas: HTMLCanvasElement;
  private coarse: boolean;
  private pointerTrail: THREE.Mesh[] = [];
  private pointerTrailGeo: THREE.SphereGeometry;
  private deviationFlash = 0;
  // v6 — three parallax starfield layers (far/mid/near) so the focused
  // glyph plane reads against real depth, not a flat dark-indigo void.
  // Per memory feedback-sibling-games-shallow §"real 3D scene with
  // depth cues"; Sigil is intimate so densities/sizes are modest.
  private starLayers: THREE.Points[] = [];
  /** v5 — populated when constructed with a ghost option. Drives the
   *  faint ghost-cursor playback against the same seeded glyphs. */
  private ghost: { samples: SigilSample[]; coarse: boolean } | null = null;
  private ghostMesh?: THREE.Mesh;
  private ghostMat?: THREE.MeshBasicMaterial;
  private ghostSampleIdx = 0;
  private ghostTrail: THREE.Mesh[] = [];
  /** v5 — opt-in pressure mode (frozen at construction). When true,
   *  per-sample pointer pressure widens the tight bucket by up to
   *  PRESSURE_WIDEN_MAX. Defaults to false. */
  private pressureMode = false;
  /** v5 — last seen pointer pressure (0..1, defaults to 0.5 for
   *  unpressured devices). Captured on every pointermove. */
  private lastPressure = 0.5;
  private static readonly PRESSURE_WIDEN_MAX = 0.1;
  private static readonly PRESSURE_WIDEN_SLOPE = 0.2;
  /** v4 — last vertex index we played a note for, per glyph. Used to
   *  trigger note playback at each integer vertex crossing. */
  private lastNoteVertex = -1;
  /** v4 — pentatonic note set in semitone offsets from a root. Index
   *  into vertices % scale.length picks the note. */
  private static readonly PENTATONIC_SEMITONES = [0, 2, 4, 7, 9, 12, 14, 16, 19, 21];

  private onPointer = (e: PointerEvent) => this.updatePointer(e);

  constructor(canvas: HTMLCanvasElement, cb: SigilCallbacks, seed = Math.floor(Math.random() * 0x7fffffff), opts: SigilOptions = {}) {
    this.canvas = canvas;
    this.cb = cb;
    this.glyphs = generateBatch(seed);
    this.ghost = opts.ghost ?? null;
    this.pressureMode = opts.pressureMode === true;
    // In ghost replay, the recorded run's `coarse` flag wins so bands match
    // the original (otherwise pixel-of-tight visual feedback would drift).
    this.coarse = this.ghost ? this.ghost.coarse : (typeof matchMedia !== "undefined" && matchMedia("(pointer: coarse)").matches);

    this.engine = new Engine(canvas, { onFrame: (dt, t) => this.frame(dt, t) });
    // Pitched camera looking down at the glyph plane.
    this.engine.camera.position.set(0, -16, 16);
    this.engine.camera.lookAt(0, 2, 0);
    this.engine.camera.fov = 56;
    this.engine.camera.updateProjectionMatrix();
    this.engine.scene.fog = new THREE.Fog(0x020812, 18, 50);
    this.engine.scene.background = new THREE.Color(0x020812);

    const ambient = new THREE.AmbientLight(0x506080, 0.5);
    this.engine.scene.add(ambient);
    const key = new THREE.DirectionalLight(0xfff0d0, 0.85);
    key.position.set(6, 12, 8);
    this.engine.scene.add(key);

    // Floor disc beneath the glyph.
    const floorGeo = new THREE.CircleGeometry(14, 64);
    const floorMat = new THREE.MeshStandardMaterial({ color: 0x010614, roughness: 0.92, metalness: 0.18 });
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -0.6;
    this.engine.scene.add(floor);

    // v6 — parallax starfield. Three THREE.Points layers at varying Z
    // give the focused glyph plane real distance behind it. Densities
    // are modest (350/180/80) so the stars read as atmosphere without
    // pulling the eye off the glyph; palette is cool to match the dark
    // indigo background. No per-frame cost — single static draw.
    // Mirrors Phalanx 2bc3ebc shape, tuned down for Sigil's intimacy.
    const makeStars = (count: number, z: number, radius: number, sizes: { size: number; color: number }) => {
      const positions = new Float32Array(count * 3);
      let s = (seed ^ Math.imul(z | 0, 0x9e3779b1)) | 0;
      const r = () => {
        s = (s + 0x6d2b79f5) | 0;
        let t = s;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
      };
      for (let i = 0; i < count; i++) {
        positions[i * 3] = (r() - 0.5) * radius * 2;
        positions[i * 3 + 1] = (r() - 0.5) * radius * 2;
        positions[i * 3 + 2] = z + (r() - 0.5) * 8;
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      const mat = new THREE.PointsMaterial({
        color: sizes.color, size: sizes.size, sizeAttenuation: true,
        transparent: true, opacity: 0.8, depthWrite: false, fog: false,
      });
      const pts = new THREE.Points(geo, mat);
      this.engine.scene.add(pts);
      this.starLayers.push(pts);
    };
    makeStars(350, -200, 220, { size: 0.5,  color: 0x6a78a0 });  // far  — cool blue/gray
    makeStars(180, -100, 130, { size: 0.4,  color: 0x8a92b8 });  // mid
    makeStars( 80,  -50,  70, { size: 0.35, color: 0xa8b0d8 });  // near

    // Traveler — emissive sphere.
    const travelerGeo = new THREE.SphereGeometry(0.4, 18, 14);
    this.travelerMat = new THREE.MeshStandardMaterial({ color: 0xff5b3a, emissive: 0xff2010, emissiveIntensity: 1.6, roughness: 0.3, metalness: 0.5 });
    this.traveler = new THREE.Mesh(travelerGeo, this.travelerMat);
    this.engine.scene.add(this.traveler);

    // Cursor — 3D ring marking pointer position on the glyph plane.
    const cursorGeo = new THREE.RingGeometry(0.35, 0.5, 28);
    this.cursorMat = new THREE.MeshBasicMaterial({ color: 0x00e0ff, transparent: true, opacity: 0.85, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
    this.cursor = new THREE.Mesh(cursorGeo, this.cursorMat);
    this.cursor.rotation.x = -Math.PI / 2;
    this.engine.scene.add(this.cursor);

    this.pointerTrailGeo = new THREE.SphereGeometry(0.12, 8, 6);

    if (this.ghost) {
      // Faint white ghost cursor. Slightly larger ring so it reads
      // distinctly from the player's own cursor would.
      const gGeo = new THREE.RingGeometry(0.42, 0.6, 28);
      this.ghostMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.32, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
      this.ghostMesh = new THREE.Mesh(gGeo, this.ghostMat);
      this.ghostMesh.rotation.x = -Math.PI / 2;
      this.engine.scene.add(this.ghostMesh);
    }

    this.buildGlyphTube();
    // No pointer listeners in ghost mode — replay is passive playback.
    if (!this.ghost) {
      canvas.addEventListener("pointermove", this.onPointer);
      canvas.addEventListener("pointerdown", this.onPointer);
    }
  }

  private buildGlyphTube() {
    if (this.glyphTube) {
      this.engine.scene.remove(this.glyphTube);
      this.glyphTube.geometry.dispose();
    }
    const g = this.glyphs[this.currentGlyph];
    // Build a closed CatmullRom curve through the glyph vertices.
    const pts: THREE.Vector3[] = g.vertices.map((v) => new THREE.Vector3(v.x, 0, v.y));
    const curve = new THREE.CatmullRomCurve3(pts, true, "catmullrom", 0.5);
    const tubeGeo = new THREE.TubeGeometry(curve, 240, 0.15, 12, true);
    const palette = GLYPH_PALETTES[this.currentGlyph % GLYPH_PALETTES.length];
    this.glyphMat = new THREE.MeshStandardMaterial({
      color: palette.line,
      emissive: palette.line,
      emissiveIntensity: 0.7,
      roughness: 0.4,
      metalness: 0.5,
    });
    // v5 — trailing fade. TubeGeometry's uv.x is the curve parameter
    // (0..1, wraps at the seam). We inject a uTravelerU uniform and a
    // smoothstep behind the traveler that dims diffuse + emissive. The
    // uniform reference is held on the Sigil instance so frame() can
    // poke it without re-reading the shader.
    this.glyphTravelerU = { value: 0 };
    const uTravelerU = this.glyphTravelerU;
    this.glyphMat.onBeforeCompile = (shader) => {
      shader.uniforms.uTravelerU = uTravelerU;
      shader.vertexShader = `varying float vCurveU;\n${shader.vertexShader}`.replace(
        "#include <uv_vertex>",
        "#include <uv_vertex>\n  vCurveU = uv.x;",
      );
      shader.fragmentShader = `uniform float uTravelerU;\nvarying float vCurveU;\n${shader.fragmentShader}`.replace(
        "#include <opaque_fragment>",
        `
        #include <opaque_fragment>
        // Traveler moves monotonically 0 → 1 along the curve once per
        // glyph (no wrap; glyph completes at u=1). Fragments with
        // vCurveU < uTravelerU are "passed" and fade with distance.
        float _trailFade = 1.0;
        if (vCurveU < uTravelerU) {
          _trailFade = 0.15 + smoothstep(uTravelerU - 0.35, uTravelerU - 0.02, vCurveU) * 0.85;
        }
        gl_FragColor.rgb *= _trailFade;
        `,
      );
    };
    this.glyphTube = new THREE.Mesh(tubeGeo, this.glyphMat);
    this.engine.scene.add(this.glyphTube);
    // Update traveler color too.
    this.travelerMat.color.setHex(palette.traveler);
    this.travelerMat.emissive.setHex(palette.emissive);
  }

  private updatePointer(e: PointerEvent) {
    const rect = this.canvas.getBoundingClientRect();
    const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const ny = -(((e.clientY - rect.top) / rect.height) * 2 - 1);
    // Project onto glyph plane (y=0).
    const ndc = new THREE.Vector3(nx, ny, 0.5).unproject(this.engine.camera);
    const dir = ndc.sub(this.engine.camera.position).normalize();
    const dist = -this.engine.camera.position.y / dir.y;
    const wx = this.engine.camera.position.x + dir.x * dist;
    const wz = this.engine.camera.position.z + dir.z * dist;
    // Game stores pointer as (x, y) but world maps to (x, z) — keep names
    // consistent with the scoring sample format.
    this.pointer.set(wx, wz);
    // v5 — capture pressure. PointerEvent.pressure is 0..1; mouse / touch
    // without force report 0.5 by spec on a pen-capable browser or 0 on
    // the depressed-button path. Coerce nonfinite / out-of-range to 0.5.
    if (this.pressureMode) {
      const p = (e.pressure > 0 && Number.isFinite(e.pressure)) ? e.pressure : 0.5;
      this.lastPressure = Math.max(0, Math.min(1, p));
    }
  }

  start() {
    this.audio.arm();
    // v6 — baseline drone; frame() ramps this up with run progress.
    this.audio.setDroneIntensity(0.15);
    this.currentGlyph = 0;
    this.vertexIdxF = 0;
    this.inBreak = true;
    this.breakEndsAt = performance.now() + 600;
    this.samples = [];
    this.combo = 0;
    this.bestCombo = 0;
    this.scoreEstimate = 0;
    this.bucketCounts = { tight: 0, close: 0, loose: 0, lost: 0 };
    this.gameOver = false;
    this.deviationFlash = 0;
    for (const t of this.pointerTrail) {
      this.engine.scene.remove(t);
      (t.material as THREE.Material).dispose();
    }
    this.pointerTrail = [];
    this.buildGlyphTube();
    if (this.samplerTimer) clearInterval(this.samplerTimer);
    if (!this.ghost) {
      this.samplerTimer = window.setInterval(() => this.takeSample(), SAMPLE_INTERVAL_MS);
    }
    this.ghostSampleIdx = 0;
    this.glyphStartedAt = performance.now();
    this.engine.start();
  }

  private bands(): [number, number, number] {
    return this.coarse ? [0.625, 1.25, 2.5] : [0.5, 1.0, 2.0];
  }

  private takeSample() {
    if (this.gameOver || this.inBreak) return;
    const t = Math.round(performance.now() - this.glyphStartedAt);
    const g = this.glyphs[this.currentGlyph];
    const vIdx = Math.floor(this.vertexIdxF) % g.vertices.length;
    const tv = g.vertices[vIdx];
    const dx = Math.round((this.pointer.x - tv.x) / QUANT) * QUANT;
    const dy = Math.round((this.pointer.y - tv.y) / QUANT) * QUANT;
    const sample: SigilSample = { t, glyph_idx: this.currentGlyph, vertex_idx: vIdx, dx, dy };
    let tightWiden = 0;
    if (this.pressureMode) {
      // Quantize p to the same 0.05 grid as dx/dy so wire bytes stay
      // consistent and server validation passes without surprises.
      const pq = Math.round(this.lastPressure / QUANT) * QUANT;
      sample.p = Math.max(0, Math.min(1, pq));
      const w = (sample.p - 0.5) * Sigil.PRESSURE_WIDEN_SLOPE;
      tightWiden = Math.max(0, Math.min(Sigil.PRESSURE_WIDEN_MAX, w));
    }
    this.samples.push(sample);

    const dev = Math.hypot(dx, dy);
    const [b0, b1, b2] = this.bands();
    let bucket: string;
    if (dev <= b0 + tightWiden) bucket = "tight";
    else if (dev <= b1) bucket = "close";
    else if (dev <= b2) bucket = "loose";
    else bucket = "lost";
    this.bucketCounts[bucket] += 1;
    if (bucket === "lost") { this.combo = 0; this.deviationFlash = 1; }
    else if (bucket === "loose") { /* no change */ }
    else if (bucket === "close") this.combo += 1;
    else { this.combo += 2; this.deviationFlash = Math.min(0.4, this.deviationFlash); }
    if (this.combo > this.bestCombo) this.bestCombo = this.combo;
    const mult = Math.min(3, 1 + this.combo * 0.05);
    const weight = bucket === "tight" ? 20 : bucket === "close" ? 10 : bucket === "loose" ? 2 : 0;
    this.scoreEstimate += Math.round(weight * mult);

    // Spawn a trail dot at the pointer position (capped at 50).
    if (this.pointerTrail.length < 50 && bucket !== "lost") {
      const palette = GLYPH_PALETTES[this.currentGlyph % GLYPH_PALETTES.length];
      const m = new THREE.Mesh(this.pointerTrailGeo, new THREE.MeshBasicMaterial({
        color: bucket === "tight" ? palette.traveler : 0xffffff,
        transparent: true,
        opacity: 0.85,
        blending: THREE.AdditiveBlending,
      }));
      m.position.set(this.pointer.x, 0.15, this.pointer.y);
      this.engine.scene.add(m);
      this.pointerTrail.push(m);
    }

    this.cb.onHud(this.currentGlyph, bucket, this.scoreEstimate);
  }

  private frame(dt: number, _t: number) {
    if (this.gameOver) return;
    const now = performance.now();

    // Decay trail dots.
    for (let i = this.pointerTrail.length - 1; i >= 0; i--) {
      const m = this.pointerTrail[i];
      const mat = m.material as THREE.MeshBasicMaterial;
      mat.opacity -= dt * 1.4;
      m.scale.multiplyScalar(1 - dt * 0.3);
      if (mat.opacity <= 0.01) {
        this.engine.scene.remove(m);
        mat.dispose();
        this.pointerTrail.splice(i, 1);
      }
    }

    // Deviation flash decay (drives traveler emissive when accurate).
    if (this.deviationFlash > 0) this.deviationFlash = Math.max(0, this.deviationFlash - dt * 1.8);
    this.travelerMat.emissiveIntensity = 1.0 + (1 - this.deviationFlash) * 1.5 + Math.sin(now * 0.008) * 0.2;

    if (this.inBreak) {
      if (now >= this.breakEndsAt) {
        this.inBreak = false;
        this.vertexIdxF = 0;
        this.glyphStartedAt = now;
      }
      return;
    }

    const g = this.glyphs[this.currentGlyph];
    this.vertexIdxF += g.speed_vps * dt;
    // v6 — adaptive bed + post-processing boost track overall run
    // progress. Per memory feedback-sibling-games-shallow §"at least
    // one escalation hook": as the player advances through glyphs,
    // bloom/afterimage and the audio drone thicken together. Capped
    // at 0.7 drone (0.15 baseline + 0.55 ramp); setBoost is damped
    // internally by the Engine.
    const overallProgress = (this.currentGlyph + Math.min(1, this.vertexIdxF / g.vertices.length)) / GLYPHS_PER_RUN;
    this.engine.setBoost(overallProgress, dt);
    this.audio.setDroneIntensity(0.15 + overallProgress * 0.55);
    // v5 — drive the trailing-fade uniform from the traveler's
    // normalized curve position.
    this.glyphTravelerU.value = (this.vertexIdxF / g.vertices.length) % 1;
    if (this.vertexIdxF >= g.vertices.length) {
      this.audio.chime(7, 0);
      if (this.currentGlyph + 1 >= GLYPHS_PER_RUN) { this.finish(); return; }
      this.currentGlyph += 1;
      this.lastNoteVertex = -1;
      this.buildGlyphTube();
      this.inBreak = true;
      this.breakEndsAt = now + GLYPH_BREAK_MS;
      return;
    }

    const vIdx = Math.floor(this.vertexIdxF) % g.vertices.length;
    const nextIdx = (vIdx + 1) % g.vertices.length;
    const frac = this.vertexIdxF - Math.floor(this.vertexIdxF);
    const a = g.vertices[vIdx];
    const b = g.vertices[nextIdx];
    // v4: melodic note on vertex crossing. Each vertex maps to an
    // entry in the pentatonic scale, biased upward by glyph index so
    // later glyphs feel higher / brighter.
    if (vIdx !== this.lastNoteVertex && this.deviationFlash < 0.5) {
      const scale = Sigil.PENTATONIC_SEMITONES;
      const note = scale[vIdx % scale.length] + this.currentGlyph * 5;
      this.audio.chime(note, this.deviationFlash * 0.4 - 0.2);
      this.lastNoteVertex = vIdx;
    }
    // Place traveler/cursor in XZ plane (camera looks down y).
    this.traveler.position.set(a.x + (b.x - a.x) * frac, 0.4, a.y + (b.y - a.y) * frac);
    this.cursor.position.set(this.pointer.x, 0.05, this.pointer.y);
    // Color the cursor by recent bucket: red if deviating.
    this.cursorMat.color.setHex(this.deviationFlash > 0.5 ? 0xff5b3a : 0x00e0ff);
    this.cursorMat.opacity = 0.7 + Math.sin(now * 0.012) * 0.15;

    if (this.ghost && this.ghostMesh && this.ghostMat) this.tickGhost(now, g, vIdx, nextIdx, frac);
  }

  private tickGhost(now: number, g: Glyph, vIdx: number, nextIdx: number, frac: number) {
    if (!this.ghost || !this.ghostMesh || !this.ghostMat) return;
    const samples = this.ghost.samples;
    const tNow = now - this.glyphStartedAt;
    // Advance ghostSampleIdx to the latest sample in the current glyph
    // whose t <= tNow. Skip ahead through past glyphs too.
    while (
      this.ghostSampleIdx < samples.length - 1 &&
      (samples[this.ghostSampleIdx + 1].glyph_idx < this.currentGlyph ||
       (samples[this.ghostSampleIdx + 1].glyph_idx === this.currentGlyph &&
        samples[this.ghostSampleIdx + 1].t <= tNow))
    ) {
      this.ghostSampleIdx += 1;
    }
    const s = samples[this.ghostSampleIdx];
    if (!s || s.glyph_idx !== this.currentGlyph) {
      this.ghostMat.opacity = 0; // no sample yet for this glyph
      return;
    }
    // Ghost position = original-traveler-pos-at-sample + (dx, dy). Since
    // glyphs are deterministic from seed, we re-derive original traveler
    // pos from sample.vertex_idx.
    const tv = g.vertices[s.vertex_idx];
    const ghostX = tv.x + s.dx;
    const ghostY = tv.y + s.dy;
    this.ghostMesh.position.set(ghostX, 0.08, ghostY);
    this.ghostMat.opacity = 0.28 + Math.sin(now * 0.01) * 0.06;
    // Sparse faint trail dots so motion reads even when the player
    // tracked perfectly (in which case ghost overlaps traveler).
    if (this.ghostTrail.length < 30 && Math.random() < 0.18) {
      const m = new THREE.Mesh(this.pointerTrailGeo, new THREE.MeshBasicMaterial({
        color: 0xffffff, transparent: true, opacity: 0.35, blending: THREE.AdditiveBlending,
      }));
      m.position.set(ghostX, 0.05, ghostY);
      m.scale.setScalar(0.7);
      this.engine.scene.add(m);
      this.ghostTrail.push(m);
    }
    // Decay ghost trail.
    for (let i = this.ghostTrail.length - 1; i >= 0; i--) {
      const m = this.ghostTrail[i];
      const mat = m.material as THREE.MeshBasicMaterial;
      mat.opacity -= 0.012;
      if (mat.opacity <= 0.01) {
        this.engine.scene.remove(m);
        mat.dispose();
        this.ghostTrail.splice(i, 1);
      }
    }
    // Silence the unused-var warning for nextIdx/frac — the original
    // traveler segment is conveyed via sample.vertex_idx, not the live
    // interpolation params.
    void nextIdx; void frac; void vIdx;
  }

  private finish() {
    this.gameOver = true;
    clearInterval(this.samplerTimer);
    this.audio.setDroneIntensity(0);
    if (this.ghost) {
      this.cb.onReplayEnd?.();
      return;
    }
    this.cb.onGameOver({
      samples: this.samples,
      scoreEstimate: this.scoreEstimate,
      bestCombo: this.bestCombo,
      bucketCounts: this.bucketCounts,
      coarse: this.coarse,
      pressureMode: this.pressureMode,
    });
  }

  setMuted(b: boolean) { this.audio.setMuted(b); }
  setVolume(v: number) { this.audio.setVolume(v); }
  armAudio() { this.audio.arm(); }

  dispose() {
    this.canvas.removeEventListener("pointermove", this.onPointer);
    this.canvas.removeEventListener("pointerdown", this.onPointer);
    if (this.samplerTimer) clearInterval(this.samplerTimer);
    // v6 — drop the adaptive drone + parallax-starfield layers so a
    // navigate-away → mount cycle doesn't accumulate state.
    this.audio.setDroneIntensity(0);
    for (const layer of this.starLayers) {
      this.engine.scene.remove(layer);
      layer.geometry.dispose();
      (layer.material as THREE.Material).dispose();
    }
    this.starLayers = [];
    for (const t of this.pointerTrail) {
      this.engine.scene.remove(t);
      (t.material as THREE.Material).dispose();
    }
    for (const t of this.ghostTrail) {
      this.engine.scene.remove(t);
      (t.material as THREE.Material).dispose();
    }
    if (this.ghostMesh) {
      this.engine.scene.remove(this.ghostMesh);
      this.ghostMesh.geometry.dispose();
      this.ghostMat?.dispose();
    }
    this.pointerTrailGeo.dispose();
    this.engine.dispose();
  }
}
